import React, { useState } from 'react';
import { Form, reduxForm } from 'redux-form';
import { makeStyles } from '@mui/styles';
import { Button, Grid } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import EsmeConfigFields from './EsmeConfigFields';
import EsmeRoutingFields from './EsmeRoutingFields';
import { EsmeObject } from '../../../../models/esmeModel';
import TabPanel from '../../../../shared/components/TabPanel';
import EsmeConfigFilePreviewContainer from '../../containers/esme/EsmeConfigFilePreviewContainer';
import { ESME_FORM_NAME } from '../../../../store/form/formStateModel';
import { VersionableId } from '../../../../types/versionableId';
import { esmeFormValidationSchema } from '../../validation/esmeFormValidation';
import { zodReduxFormValidatorResolver } from '../../../../shared/forms/validation/resolvers/zodReduxFormValidatorResolver';

const useStyles = makeStyles(theme => ({
  form: {
    display: 'flex',
  },
  root: {
    paddingBottom: 20,
    width: '100%',
    height: 'auto',
    flexGrow: 1,
  },
  button: {
    marginRight: '15px'
  },
  buttons: {
    paddingBottom: '10px',
    paddingTop: '10px',
    backgroundColor: '#F5F5F5',
    width: '100%',
    position: 'fixed',
    bottom: 0,
  }
}));

export type EsmeDataForm = EsmeObject;

type Props = {
  readonly title: string;
  readonly esmeId?: VersionableId;
  readonly smscIdList: string[];
  readonly initialValues?: Partial<EsmeDataForm>;
  readonly onSubmit?: (esme: EsmeDataForm) => void;
  readonly onCancel: () => void;
  readonly readOnly: boolean;
} & {
  // redux-form props
  readonly handleSubmit?: (data: any) => void;
  readonly invalid?: boolean;
  readonly pristine?: boolean;
};

const EsmeForm = (props: Props) => {
  const {
    title,
    esmeId,
    smscIdList,
    onCancel,
    readOnly,
  } = props;

  const { handleSubmit, invalid, pristine } = props;

  const classes = useStyles();

  const [outerTab, setOuterTab] = useState<number>(0);
  const [innerTab, setInnerTab] = useState<number>(0);

  return (
    <Form onSubmit={handleSubmit}>
      <Grid container>
        <Grid item style={{ width: '100%' }}>
          <AppBar position='static'>
            <Typography variant='h6' style={{ padding: '10px 20px' }}>ESME</Typography>
          </AppBar>
        </Grid>
      </Grid>
      <Grid container>
        <Paper style={{ backgroundColor: '#F5F5F5', width: '100%' }}>
          <Tabs
            style={{ minHeight: '100%' }}
            value={outerTab}
            onChange={(event, value) => setOuterTab(value)}
            indicatorColor='primary'
            textColor='primary'
            variant='scrollable'
          >
            <Tab label={title} />
            <Tab label='Preview' />
          </Tabs>
        </Paper>
        <div className={classes.root}>
          <Grid item xs={12} style={{ paddingRight: 25, paddingLeft: 25 }}>
            <Grid container spacing={3} style={{ display: 'flex', paddingTop: 40 }}>
              <Grid container style={{ paddingRight: 25, paddingLeft: 25 }}>
                <Grid item style={{ width: '100%' }}>
                  <div>
                    <TabPanel value={outerTab} index={0}>
                      <Paper style={{ backgroundColor: '#F5F5F5', width: '100%' }}>
                        <Tabs
                          style={{ minHeight: '100%' }}
                          value={innerTab}
                          onChange={(event, value) => setInnerTab(value)}
                          indicatorColor='primary'
                          textColor='primary'
                          variant='scrollable'
                        >
                          <Tab label='Config' />
                          <Tab label='Routings' />
                        </Tabs>
                      </Paper>
                      <Grid item style={{ width: '100%' }}>
                        <div>
                          <TabPanel value={innerTab} index={0}>
                            <EsmeConfigFields readOnly={readOnly} />
                          </TabPanel>
                          <TabPanel value={innerTab} index={1}>
                            <EsmeRoutingFields smscIdList={smscIdList} readOnly={readOnly} />
                          </TabPanel>
                        </div>
                      </Grid>
                    </TabPanel>
                    <TabPanel value={outerTab} index={1}>
                      <EsmeConfigFilePreviewContainer esmeId={esmeId} readOnly={readOnly} />
                    </TabPanel>
                  </div>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </div>
      </Grid>
      <Container className={classes.buttons}>
        { !readOnly && (
          <Button
            type='submit'
            color='primary'
            className={classes.button}
            disabled={invalid || pristine}
          >
            Save
          </Button>
        )}
        <Button onClick={onCancel}>{ readOnly ? 'Close' : 'Cancel' }</Button>
      </Container>
    </Form>
  );
};

export default reduxForm<EsmeDataForm, Props, {}>({
  form: ESME_FORM_NAME,
  destroyOnUnmount: false,
  enableReinitialize: true,
  keepDirtyOnReinitialize: false,
  validate: (values) => (
    zodReduxFormValidatorResolver(esmeFormValidationSchema)(values)
  ),
})(EsmeForm);
